/**
 * Workspace — the board page (PR4-fidelity), matched to Amanda's prototype:
 * a per-view header (title + computed stat subtitle + search + "+ New intake"),
 * the views (Intake Pipeline / Caseload / Completed), and a card
 * drawer (?item=, the bell deep-link target). Gated on canViewWorkspace; writes
 * gate on canManageWorkItems (the server re-checks). (Workspace v3 retired the
 * "My Work" + "All tasks" views — personal work now lives on the home "My tasks"
 * agenda, HubMyTasks.)
 * The page owns the per-view fetch so the header counts and the view share one
 * cache entry.
 *
 * Workspace v2: the views are reached via a Workspace-INTERNAL left panel
 * (WorkspaceNav) — Processes (the boards) + Views (personal lenses) — in place
 * of the old horizontal tab strip. The app shell's top nav is untouched. The
 * `?view=` keys + the per-view queries are unchanged: this is a chrome swap.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canViewWorkspace, canManageWorkItems, canManageWorkspaceProcesses } from '@/lib/capabilities';
import { SectionBand } from '@/components/primitives/SectionBand';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES } from '@/components/primitives/PageShell';
import { Button } from '@/components/primitives/Button';
import { useWorkItems, usePipelineStages, useCaseload, useCreateWorkItem, useWorkspaceMembers } from '@/lib/workspace';
import { useWorkspaceProcesses, useProcessCommand } from '@/lib/workspaceProcesses';
import { workspaceProcessesApi, type CreateWorkItemInput } from '@/lib/api';
import { PipelineBoard } from '@/components/workspace/PipelineBoard';
import { NewIntakeForm } from '@/components/workspace/NewIntakeForm';
import { NewProcessDialog } from '@/components/workspace/process/NewProcessDialog';
import { NewProcessCardForm } from '@/components/workspace/process/NewProcessCardForm';
import { ProcessEditor } from '@/components/workspace/process/ProcessEditor';
import { moveBlockedMessage } from '@/components/workspace/workspaceFormat';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { CaseloadView } from '@/components/workspace/CaseloadView';
import { CompletedView } from '@/components/workspace/CompletedView';
import { HubMyTasks } from '@/components/hub/HubMyTasks';
import { WorkItemDrawer } from '@/components/workspace/WorkItemDrawer';
import { WorkspaceSearch } from '@/components/workspace/WorkspaceSearch';
import { WorkspaceNav, WorkspaceNavSelect, WORKSPACE_VIEWS, type WorkspaceView } from '@/components/workspace/WorkspaceNav';
import { cardName, isOverdue } from '@/components/workspace/workspaceFormat';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { EMPTY_PROCESS_CARD_FIELDS, readProcessCardFields, type ProcessCardFields } from '@/lib/processCardFields';
import type { WorkItemRow } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';

function matches(name: string, q: string): boolean {
  return q.trim() === '' || name.toLowerCase().includes(q.trim().toLowerCase());
}

export function Workspace(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [newIntakeOpen, setNewIntakeOpen] = useState(false);
  // My Tasks header CTA: each bump opens HubMyTasks' inline add-task form.
  const [addTaskSignal, setAddTaskSignal] = useState(0);
  // editable-workspace-processes (PR3): board edit mode + the two create dialogs.
  const [editMode, setEditMode] = useState(false);
  const [newProcessOpen, setNewProcessOpen] = useState(false);
  const [newProcessError, setNewProcessError] = useState<string | null>(null);
  const [newCardOpen, setNewCardOpen] = useState(false);
  const [newCardError, setNewCardError] = useState<string | null>(null);
  // Field pack v1: the board's category filter ('' = all). Board-local —
  // reset on every board switch (setProcess + the deep-link effect below).
  const [categoryFilter, setCategoryFilter] = useState('');

  // Derive access — do NOT early-return yet (whoami loads async; an early
  // return here would run fewer hooks pre-auth than post-auth → React #310,
  // "rendered more hooks than during the previous render"). Gate AFTER the hooks.
  const hasAccess = canViewWorkspace(whoami);
  // Reads only the shared config cache for a person the page turns away.
  const contact = useSupportContact({ enabled: hasAccess });
  const viewParam = searchParams.get('view');
  // Default landing = My Tasks (founder ruling 2026-07-06): /workspace with no
  // ?view= opens the personal agenda; the Intake Pipeline stays one click away
  // in the rail. Deep-links (?view=pipeline etc.) are unchanged.
  const view: WorkspaceView = WORKSPACE_VIEWS.includes(viewParam as WorkspaceView)
    ? (viewParam as WorkspaceView)
    : 'mytasks';
  const selectedItemId = searchParams.get('item');
  const canManage = canManageWorkItems(whoami);
  const canManageProcesses = canManageWorkspaceProcesses(whoami);

  // editable-workspace-processes (PR3): the process switcher. The intake
  // (is_system) board is the default; ?process= selects another. Before the
  // list loads (or when it's just intake) the board behaves byte-identically
  // to the pre-PR3 single-process page.
  const processesQ = useWorkspaceProcesses(tenantSlug, { enabled: hasAccess });
  const processParam = searchParams.get('process');
  const intakeProcess = processesQ.processes.find((p) => p.is_system) ?? null;
  const selectedProcess =
    (processParam ? processesQ.processes.find((p) => p.id === processParam) : null) ?? intakeProcess;
  const selectedProcessId = selectedProcess?.id ?? null;
  const isIntakeBoard = !selectedProcess || selectedProcess.is_system;

  // Per-view data — ALWAYS called (Rule of Hooks); enabled-gated on access + the
  // active view so a no-access (or pre-auth-load) user neither fetches nor
  // changes the hook count between renders.
  // Slice 2: the active board is in-flight only — bucket:'active' drops the
  // terminal Assigned + Closed cases (they live in the Completed view).
  const intakeQ = useWorkItems(tenantSlug, { kind: 'intake', bucket: 'active', limit: 100 }, { enabled: hasAccess && view === 'pipeline' && isIntakeBoard });
  // A non-intake board reads its process_item cards (visibility-checked server-side).
  const processCardsQ = useWorkItems(tenantSlug, { process: selectedProcessId ?? undefined, limit: 100 }, { enabled: hasAccess && view === 'pipeline' && !isIntakeBoard && !!selectedProcessId });
  const stagesQ = usePipelineStages(tenantSlug, { enabled: hasAccess && view === 'pipeline', processId: isIntakeBoard ? undefined : (selectedProcessId ?? undefined) });
  const caseloadQ = useCaseload(tenantSlug, { enabled: hasAccess && view === 'caseload' });
  const { members } = useWorkspaceMembers(tenantSlug, { enabled: hasAccess && canManage });
  const createCard = useCreateWorkItem(tenantSlug);
  const createProcessCmd = useProcessCommand<{ process?: { id: string } | null }>(tenantSlug);

  const boardRows = isIntakeBoard ? intakeQ.rows : processCardsQ.rows;
  const boardLoading = isIntakeBoard ? intakeQ.isLoading : processCardsQ.isLoading;

  // Field pack v1: per-board card-field config (a categorical field + a
  // due-date label), keyed by the process's immutable key in
  // tenants.config.workspace.process_card_fields. Unconfigured/intake
  // boards resolve to nulls and render byte-identically to before.
  const tenantConfigQ = useTenantConfig(tenantSlug, { enabled: hasAccess && view === 'pipeline' && !isIntakeBoard });
  const cardFields: ProcessCardFields =
    !isIntakeBoard && selectedProcess
      ? readProcessCardFields(tenantConfigQ.data?.config, selectedProcess.key)
      : EMPTY_PROCESS_CARD_FIELDS;

  // A board switch (rail click or deep-link) drops the previous board's
  // category filter — values are per-board vocabulary.
  useEffect(() => {
    setCategoryFilter('');
  }, [selectedProcessId]);

  // Leaving the pipeline view closes edit mode + the new-card form so neither
  // lingers onto another surface. (A board SWITCH keeps edit mode — the
  // new-process flow lands on its new board in edit mode; setProcess closes the
  // per-board new-card dialog on its own.)
  useEffect(() => {
    setEditMode(false);
    setNewCardOpen(false);
  }, [view]);

  // Conversion left the Workspace views for the Reports page (founder move
  // ruling 2026-07-14). Old ?view=conversion deep-links hop there, preserving
  // every other query param (?item= keeps working — Reports hosts the drawer).
  // Placed AFTER all hooks (the AdminReviewRedirect param-preserving
  // precedent; never early-return above hooks — React #310).
  if (viewParam === 'conversion') {
    const sp = new URLSearchParams(searchParams);
    sp.delete('view');
    sp.set('tab', 'conversion');
    return <Navigate to={`/t/${encodeURIComponent(tenantSlug)}/reports/referrals?${sp.toString()}`} replace />;
  }

  // Gate AFTER all hooks have run.
  if (!hasAccess) {
    return (
      <div className="px-3 py-10 max-w-3xl mx-auto text-center" data-testid="workspace-denied">
        <p className="text-sm text-ink-soft">
          {contactSentence("The Workspace isn't part of your access. Ask {contact_admin} if your work needs it.", contact)}
        </p>
      </div>
    );
  }

  function setView(next: WorkspaceView): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('view', next);
    setSearchParams(sp, { replace: true });
  }
  // PR3: select a process board — lands on the pipeline view with ?process=.
  // The intake (is_system) board drops the param so its URL stays clean.
  function setProcess(processId: string): void {
    setNewCardOpen(false); // close a per-board dialog when the board changes
    const sp = new URLSearchParams(searchParams);
    sp.set('view', 'pipeline');
    if (intakeProcess && processId === intakeProcess.id) sp.delete('process');
    else sp.set('process', processId);
    setSearchParams(sp, { replace: true });
  }
  function openItem(id: string): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('item', id);
    setSearchParams(sp);
  }
  function closeItem(): void {
    const sp = new URLSearchParams(searchParams);
    sp.delete('item');
    setSearchParams(sp, { replace: true });
  }

  // Title + computed stat subtitle per view.
  const stageKeyById = new Map(stagesQ.stages.map((s) => [s.id, s.key]));
  function needsAttention(r: WorkItemRow): boolean {
    const sk = r.stage_id ? stageKeyById.get(r.stage_id) : null;
    return isOverdue(r) || (sk === 'ready_for_assignment' && !r.assignee_roster_id);
  }
  // A shared client is listed under each of its care managers, so the header
  // counts each client id once.
  const totalClients = new Set(caseloadQ.careManagers.flatMap((cm) => cm.clients.map((c) => c.id))).size;
  // Pipeline header: intake keeps its "N active · M need attention"; a non-intake
  // board shows a plain card count (no intake-specific attention semantics).
  const pipelineTitle = selectedProcess?.label ?? 'Intake Pipeline';
  const pipelineSubtitle = isIntakeBoard
    ? `${intakeQ.rows.length} active intakes · ${intakeQ.rows.filter(needsAttention).length} need attention`
    : `${processCardsQ.rows.length} ${processCardsQ.rows.length === 1 ? 'card' : 'cards'}`;
  const meta: Record<WorkspaceView, [string, string | null]> = {
    pipeline: [pipelineTitle, pipelineSubtitle],
    mytasks: ['My Tasks', null],
    caseload: ['Caseload', `${caseloadQ.careManagers.length} care managers · ${totalClients} active clients`],
    completed: ['Completed', null],
  };

  // SEARCH ENGINE SPLIT (Slice 2, tech-debt #6): the active views use this
  // CLIENT-SIDE in-memory filter over already-loaded rows; the Completed view uses
  // a SERVER-SIDE trigram search (listWorkItems `q`). Same search box, different
  // engine — mirrors the Directory (providerSearch.ts vs useUnifiedSearch, ADR-015).
  // Don't "fix" one by changing the other. A THIRD engine (founder soak, 0706)
  // rides the same box: WorkspaceSearch layers a whole-workspace results
  // dropdown (clients + tasks + people) over these per-view filters, so the
  // placeholder's promise fires on every view.
  // Field pack v1: the category select ANDs into the same in-memory filter
  // (non-intake boards only — the control renders only there, so the
  // filter is '' everywhere else).
  const filteredIntakes = boardRows.filter(
    (r) => matches(cardName(r), query) && (categoryFilter === '' || (r.categories ?? [r.category]).includes(categoryFilter)),
  );
  const filteredCaseload = caseloadQ.careManagers.filter(
    (cm) => matches(cm.full_name, query) || cm.clients.some((c) => matches(c.display_name, query)),
  );

  // Band primary: "+ Task" works from every view — it lands on My Tasks
  // and opens the same inline add form the bottom-of-list affordance opens.
  function addTaskFromBand(): void {
    if (view !== 'mytasks') setView('mytasks');
    setAddTaskSignal((s) => s + 1);
  }

  return (
    <div data-testid="workspace-page">
      {/* DL v1 section band (nav → band → body, no exceptions). The
          workspace-internal sidebar below is the one sanctioned sidebar
          layout — the band sits above it, not inside it. */}
      {/* Founder soak: the board wants the whole screen — the Workspace takes
          the DL9 `wide` axis (the cockpit-class width, with the 2xl ultra-wide
          step-up that hugs the viewport), band + body together so they keep
          ONE left edge. Other pages stay on their own widths. */}
      <SectionBand
        testId="workspace-band"
        width="wide"
        eyebrow="Olive · Work"
        title="Workspace"
        search={<WorkspaceSearch value={query} onChange={setQuery} />}
        action={
          <Button
            // The on-dark PRIMARY treatment: "+ Task" is this page's one
            // primary action, so on the forest band it takes primary-on-dark
            // (cream fill, forest text), matching "+ Case Note" (Suggest stays
            // the ghost).
            variant="primary-on-dark"
            data-testid="add-task-button"
            onClick={addTaskFromBand}
          >
            + Task
          </Button>
        }
      />
      <div
        data-testid="workspace-body"
        className={`${PAGE_GUTTER} py-5 ${PAGE_WIDTH_CLASSES.wide} mx-auto`}
      >
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
        {/* Workspace-INTERNAL left panel (Processes / Views) — in place of the
            prototype's sidebar. The app shell's top nav (AppShell) is untouched. */}
        <WorkspaceNav
          view={view}
          onSelect={setView}
          processes={processesQ.processes}
          selectedProcessId={selectedProcessId}
          onSelectProcess={setProcess}
          canManageProcesses={canManageProcesses}
          onNewProcess={() => { setNewProcessError(null); setNewProcessOpen(true); }}
        />
        {/* Mobile view-switcher: replaces the rail below md (which is hidden). */}
        <WorkspaceNavSelect
          view={view}
          onSelect={setView}
          processes={processesQ.processes}
          selectedProcessId={selectedProcessId}
          onSelectProcess={setProcess}
          className="w-full md:hidden"
        />

        <section
          id="workspace-tabpanel"
          role="tabpanel"
          tabIndex={0}
          aria-label={meta[view][0]}
          className="flex-1 min-w-0 w-full focus-visible:outline-none"
        >
          {/* Per-view header. With the band carrying the page's display
              title, the view title takes the SECTION-HEADER type step (h2)
              — equal page rank, equal step. The page search + "+ Task" now
              live on the band; "+ New intake" stays view-contextual (it
              belongs to the Intake Pipeline). */}
          <div className="flex items-center gap-4 mb-4">
            <div className="min-w-0">
              <h2 className="font-display text-xl font-semibold text-sage-800 leading-tight">{meta[view][0]}</h2>
              {meta[view][1] !== null && (
                <div className="text-[12.5px] text-sage-400 mt-0.5" data-testid="view-subtitle">{meta[view][1]}</div>
              )}
            </div>
            {view === 'completed' && (
              <Link
                data-testid="completed-conversion-link"
                to={`/t/${encodeURIComponent(tenantSlug)}/reports/referrals?tab=conversion`}
                className="ml-auto text-[12.5px] font-semibold text-sage-800 hover:underline whitespace-nowrap rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                Conversion report →
              </Link>
            )}
            {view === 'pipeline' && (
              <div className="ml-auto flex items-center gap-2">
                {/* Field pack v1: the board's category filter (config-labeled,
                    e.g. "Role"). Options = the config list ∪ any off-list
                    values already on loaded cards (grandfathered values stay
                    filterable). Non-intake boards with a configured field only. */}
                {!isIntakeBoard && cardFields.category && (
                  <SelectField size="compact"
                    data-testid="board-category-filter"
                    aria-label={`Filter by ${cardFields.category.label}`}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="">All · {cardFields.category.label}</option>
                    {[...new Set([...cardFields.category.options, ...boardRows.flatMap((r) => r.categories ?? (r.category ? [r.category] : []))])].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </SelectField>
                )}
                {/* Edit-board toggle (capability-gated). Opens the process editor. */}
                {canManageProcesses && (
                  <button
                    type="button"
                    data-testid="edit-board-toggle"
                    aria-pressed={editMode}
                    onClick={() => setEditMode((v) => !v)}
                    className={
                      'inline-flex items-center gap-1.5 rounded-[9px] px-3 py-2.5 font-semibold text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                      (editMode ? 'bg-sage-100 text-sage-800' : 'text-sage-600 hover:bg-sage-50')
                    }
                  >
                    <Pencil size={14} aria-hidden="true" /> {editMode ? 'Done editing' : 'Edit board'}
                  </button>
                )}
                {/* Intake board keeps "+ New intake"; a non-intake board gets "+ New card". */}
                {canManage && isIntakeBoard && (
                  <button type="button" data-testid="new-intake-button" aria-expanded={newIntakeOpen} aria-haspopup="dialog" onClick={() => setNewIntakeOpen(true)}
                    className="bg-sage-800 text-white rounded-[9px] px-4 py-2.5 font-semibold text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">+ New intake</button>
                )}
                {canManage && !isIntakeBoard && selectedProcessId && (
                  <button type="button" data-testid="new-card-button" aria-haspopup="dialog" onClick={() => { setNewCardError(null); setNewCardOpen(true); }}
                    className="bg-sage-800 text-white rounded-[9px] px-4 py-2.5 font-semibold text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">+ New card</button>
                )}
              </div>
            )}
          </div>

          {newIntakeOpen && canManage && <NewIntakeForm onClose={() => setNewIntakeOpen(false)} />}

          {/* Edit mode: the process editor sits above the board. */}
          {view === 'pipeline' && editMode && canManageProcesses && selectedProcess && (
            <ProcessEditor
              slug={tenantSlug}
              process={selectedProcess}
              onExit={() => setEditMode(false)}
              onArchived={() => { setEditMode(false); if (intakeProcess) setProcess(intakeProcess.id); }}
            />
          )}

          {view === 'pipeline' && (
            (boardLoading || stagesQ.isLoading)
              ? <div className="text-sm text-ink-soft px-2 py-4">Loading…</div>
              // Brief 3 Design B: Assigned is now the final WORKED column (was dropped
              // as a terminal end-state). A drag INTO Assigned still opens the Assign
              // form (resolveStageMove → 'open-assign'); the terminal "Move to
              // Completed" advance (in the drawer) closes the case out of the board.
              // An empty non-intake board (no stages yet) prompts the first stage.
              : (!isIntakeBoard && stagesQ.stages.length === 0)
                ? <EmptyBoardHint canEdit={canManageProcesses} onEdit={() => setEditMode(true)} />
                : <PipelineBoard rows={filteredIntakes} stages={stagesQ.stages} onOpenItem={openItem} categoryField={cardFields.category} dueDateLabel={cardFields.due_date_label} folderLink={cardFields.folder_link} boardKey={selectedProcess?.key ?? 'intake'} />
          )}
          {view === 'mytasks' && <HubMyTasks embedded addTaskSignal={addTaskSignal} itemHref={(id) => `/t/${tenantSlug}/workspace?view=mytasks&item=${id}`} />}
          {view === 'caseload' && (
            <CaseloadView careManagers={filteredCaseload} isLoading={caseloadQ.isLoading} isError={caseloadQ.isError} error={caseloadQ.error} onRetry={caseloadQ.refetch} />
          )}
          {view === 'completed' && (
            <CompletedView query={query} canManage={canManage} onOpen={openItem} />
          )}
        </section>
      </div>

      {selectedItemId && <WorkItemDrawer key={selectedItemId} itemId={selectedItemId} onClose={closeItem} />}

      {/* PR3 — create a new process board (lands on it in edit mode). */}
      {newProcessOpen && canManageProcesses && (
        <NewProcessDialog
          submitting={createProcessCmd.isPending}
          error={newProcessError}
          onCancel={() => setNewProcessOpen(false)}
          onCreate={(input) =>
            createProcessCmd.mutate(
              { run: () => workspaceProcessesApi.createProcess(tenantSlug, input) },
              {
                onSuccess: (res) => {
                  const id = res.process?.id;
                  setNewProcessOpen(false);
                  if (id) { setProcess(id); setEditMode(true); }
                },
                onError: () =>
                  setNewProcessError(
                    contactSentence(
                      "The board wasn't created. Try a different name, and if it still fails, tell {contact_system}.",
                      contact,
                    ),
                  ),
              },
            )
          }
        />
      )}

      {/* PR3 — "+ New card" on a non-intake board. */}
      {newCardOpen && canManage && !isIntakeBoard && selectedProcessId && (
        <NewProcessCardForm
          processId={selectedProcessId}
          stages={stagesQ.stages}
          members={members}
          categoryField={cardFields.category}
          dueDateLabel={cardFields.due_date_label}
          submitting={createCard.isPending}
          error={newCardError}
          onCancel={() => setNewCardOpen(false)}
          onCreate={(input: CreateWorkItemInput) =>
            createCard.mutate(input, {
              onSuccess: (row) => { setNewCardOpen(false); openItem(row.id); },
              onError: (err) => setNewCardError(moveBlockedMessage(err, contact)),
            })
          }
        />
      )}
      </div>
    </div>
  );
}

/** An empty non-intake board (no stages yet) — nudge the first stage. */
function EmptyBoardHint({ canEdit, onEdit }: { canEdit: boolean; onEdit: () => void }): ReactNode {
  return (
    <div className="rounded-2xl bg-sand px-6 py-10 text-center" data-testid="empty-board-hint">
      <p className="text-sm text-ink-soft">Add the first stage to start this board.</p>
      {canEdit && (
        <button type="button" data-testid="empty-board-add-stage" onClick={onEdit}
          className="mt-3 inline-flex items-center gap-1.5 bg-sage-800 text-white rounded-[9px] px-4 py-2.5 font-semibold text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
          Add the first stage
        </button>
      )}
    </div>
  );
}
